import { internalApiSlice, transformResponse } from "../../api/internalApiSlice";
import { InvoiceApproval, PurchaseOrder } from "@/types/general";
import { purchasesApi } from "@redux/features/purchases/purchasesApi";

export const invoiceApprovalApi = internalApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getUnlinkedInvoices: builder.query<InvoiceApproval.UnlinkedInvoice[], { from: string, to: string }>({
            query: (args) => ({
                url: `tripletex/unlinked-vouchers`,
                method: "GET",
                params: {
                    date_from: args.from,
                    date_to: args.to
                }
            }),
            providesTags: (result, error, args) => {
                return result?.map(invoice => {
                    return {
                        type: "ExternalInvoice",
                        id: invoice.system_id
                    };
                }) ?? [];
            },
            transformResponse
        }),

        linkInvoice: builder.mutation<InvoiceApproval.LinkedInvoice, InvoiceApproval.DTO.Link & {
            purchase_order_id: number
        }>({
            query: ({ purchase_order_id, ...body }) => ({
                url: `purchase-order/${purchase_order_id}/invoices`,
                method: "POST",
                data: body
            }),
            invalidatesTags: (result, error, args) => {
                return [{
                    type: "PurchaseOrder",
                    id: args.purchase_order_id
                }, "ExternalInvoice"];
            }
        }),

        unlinkInvoice: builder.mutation<void, InvoiceApproval.DTO.Unlink & { purchase_order_id: number }>({
            query: ({ purchase_order_id, ...body }) => ({
                url: `purchase-order/${purchase_order_id}/invoices`,
                method: "DELETE",
                params: body
            }),
            async onQueryStarted(body, { dispatch, queryFulfilled }) {
                let originalInvoicesState: PurchaseOrder.Extended["invoices"];
                try {
                    dispatch(
                        purchasesApi.util.updateQueryData("getPurchaseOrder", body.purchase_order_id, (draft) => {
                            originalInvoicesState = [...draft.invoices];
                            draft.invoices = draft.invoices.filter(invoice => invoice.system_id !== body.system_id);
                        })
                    );

                    // Awaiting for the query to finish to see whether the operation was successful or not
                    const { data: unlinkedInvoice } = await queryFulfilled;
                } catch (err) {
                    console.error(err);

                    dispatch(
                        purchasesApi.util.updateQueryData("getPurchaseOrder", body.purchase_order_id, (draft) => {
                            draft.invoices = originalInvoicesState;
                        })
                    );
                }
            },
            invalidatesTags: (result, error, args) => [{
                type: "PurchaseOrder",
                id: args.purchase_order_id
            }, "ExternalInvoice"]
        }),

        setReadyForPayment: builder.mutation<InvoiceApproval.LinkedInvoice, InvoiceApproval.DTO.SetReadyForPayment & {
            purchase_order_id: number
        }>({
            query: ({ purchase_order_id, ...body }) => ({
                url: `purchase-order/invoices/set-ready-for-payment`,
                method: "PUT",
                data: body
            }),
            invalidatesTags: (result, error, args) => [{
                type: "PurchaseOrder",
                id: args.purchase_order_id
            }]
        })
    })
});


export const {
    useGetUnlinkedInvoicesQuery,
    useLinkInvoiceMutation,
    useUnlinkInvoiceMutation,
    useSetReadyForPaymentMutation
} = invoiceApprovalApi;
